import * as Sentry from "@sentry/nextjs";
import { CRM, GENERIC_CLUSTER } from "constants/clusters";
import { HP_SIGNUP_BUTTON, SET_CLUSTER_FROM_SIGNUP_BUTTON_REASON } from "constants/clusters/sources";
import { CLUSTER_TYPE } from "constants/clusters/types";
import { SIGNUP_PRODUCT_COOKIE_NAME } from "constants/cookies";
import { PLATFORM_PRODUCT_IDS, WORK_MANAGEMENT_PRODUCT_ID } from "constants/products";
import { useEffect } from "react";
import { getProductByClustersIds, updateUserClustersListWithSingleCluster } from "services/clusters-service";
import { removeCookie, setCookie } from "services/cookies-service";
import { isDevelopment } from "services/environment-service";
import { trackEvent } from "services/bigbrain-service";
import { GOOGLE_ONE_TAP_DISPLAYED, GOOGLE_ONE_TAP_DISMISSED, GOOGLE_ONE_TAP_SKIPPED, GOOGLE_ONE_TAP_SUCCESS } from "constants/bigbrain-event-types";
// Can view event types meaning here: https://developers.google.com/identity/gsi/web/reference/js-reference
const DISPLAY_MOMENT_TYPES = {
    DISPLAY: "display",
    SKIPPED: "skipped",
    DISMISSED: "dismissed"
};
const DISPLAY_MOMENT_TO_EVENT_MAPPING = {
    [DISPLAY_MOMENT_TYPES.DISPLAY]: GOOGLE_ONE_TAP_DISPLAYED,
    [DISPLAY_MOMENT_TYPES.SKIPPED]: GOOGLE_ONE_TAP_SKIPPED,
    [DISPLAY_MOMENT_TYPES.DISMISSED]: GOOGLE_ONE_TAP_DISMISSED
};
const CLIENT_ID = process.env.GOOGLE_ONE_TAP_CLIENT_ID;
const HTTP_TYPE = isDevelopment ? "http://" : "https://";
const DOMAIN = isDevelopment ? "localhost:3000" : process.env.PLATFORM_DOMAIN;
const COOKIE_DOMAIN = `auth.${DOMAIN}`;
const LOGIN_URL = `${HTTP_TYPE}auth.${DOMAIN}/users/auth/google_one_tap/callback`;
export default function GoogleOneTap({ clusterId, subClusterId }) {
    const getSignupClusterId = ()=>{
        return clusterId || subClusterId;
    };
    const updateUserCluster = ()=>{
        const clusterToReport = getSignupClusterId() || GENERIC_CLUSTER;
        updateUserClustersListWithSingleCluster(clusterToReport, CLUSTER_TYPE, HP_SIGNUP_BUTTON, {
            setSignupCluster: true,
            reason: SET_CLUSTER_FROM_SIGNUP_BUTTON_REASON,
            sendBigBrainEvent: true,
            useCaseId: subClusterId
        });
    };
    const getSignupProductId = ()=>{
        const acceptedClustersForProductSignup = [
            CRM
        ];
        const currentClusterId = clusterId || subClusterId;
        if (currentClusterId && acceptedClustersForProductSignup.includes(currentClusterId)) {
            return getProductByClustersIds([
                currentClusterId
            ]);
        }
    };
    const handleProductCookie = ()=>{
        const productForSignup = getSignupProductId();
        if (productForSignup !== WORK_MANAGEMENT_PRODUCT_ID) {
            const platformProduct = PLATFORM_PRODUCT_IDS[productForSignup];
            if (platformProduct) {
                setCookie(SIGNUP_PRODUCT_COOKIE_NAME, platformProduct, {
                    expires: 1
                });
            }
        } else {
            removeCookie(SIGNUP_PRODUCT_COOKIE_NAME);
        }
    };
    const trackGoogleOneTapEvent = (notification)=>{
        const displayMoment = notification.getMomentType();
        const eventData = {
            kind: "google_one_tap",
            placement: window.location.href,
            info1: getSignupClusterId() || GENERIC_CLUSTER
        };
        const eventType = DISPLAY_MOMENT_TO_EVENT_MAPPING[displayMoment];
        if (eventType) {
            trackEvent(eventType, eventData);
        }
    };
    useEffect(()=>{
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = ()=>{
            try {
                window.google.accounts.id.initialize({
                    client_id: CLIENT_ID,
                    state_cookie_domain: COOKIE_DOMAIN,
                    callback: (response)=>{
                        trackEvent(GOOGLE_ONE_TAP_SUCCESS, {
                            kind: "google_one_tap",
                            placement: window.location.href,
                            info1: getSignupClusterId() || GENERIC_CLUSTER
                        });
                        updateUserCluster();
                        handleProductCookie();
                        // Create a form and submit it to avoid CORS issues
                        const form = document.createElement('form');
                        form.method = 'POST';
                        form.action = LOGIN_URL;
                        Object.keys(response).forEach((key)=>{
                            const input = document.createElement('input');
                            input.type = 'hidden';
                            input.name = key;
                            input.value = response[key];
                            form.appendChild(input);
                        });
                        document.body.appendChild(form);
                        form.submit();
                    },
                    auto_select: false,
                    cancel_on_tap_outside: false
                });
                window.google.accounts.id.prompt((notification)=>{
                    trackGoogleOneTapEvent(notification);
                });
            } catch (error) {
                Sentry.captureException(error);
                Sentry.captureMessage(`GoogleOneTap initialization error: ${(error === null || error === void 0 ? void 0 : error.message) || error}`);
            }
        };
        document.body.appendChild(script);
        return ()=>{
            document.body.removeChild(script);
        };
    }, []);
    return null;
}
